import { useState } from "react";
import type { HeatmapCell } from "@/lib/api";

interface Props {
  cells: HeatmapCell[];
  mode: "likes" | "replies" | "total";
}

const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const hourLabels = Array.from({ length: 24 }, (_, i) =>
  i === 0 ? "12a" : i < 12 ? `${i}a` : i === 12 ? "12p" : `${i - 12}p`
);

const heatColors = [
  "bg-heat-0",
  "bg-heat-1",
  "bg-heat-2",
  "bg-heat-3",
  "bg-heat-4",
  "bg-heat-5",
  "bg-heat-6",
];

function getHeatLevel(value: number, max: number): number {
  if (max === 0 || value === 0) return 0;
  const ratio = value / max;
  if (ratio < 0.05) return 0;
  if (ratio < 0.15) return 1;
  if (ratio < 0.3) return 2;
  if (ratio < 0.5) return 3;
  if (ratio < 0.7) return 4;
  if (ratio < 0.9) return 5;
  return 6;
}

export function HeatmapGrid({ cells, mode }: Props) {
  const [tooltip, setTooltip] = useState<{
    cell: HeatmapCell;
    x: number;
    y: number;
  } | null>(null);

  const getValue = (cell: HeatmapCell): number => {
    switch (mode) {
      case "likes": return cell.likes;
      case "replies": return cell.replies;
      default: return cell.total;
    }
  };

  const maxValue = Math.max(...cells.map(getValue), 1);

  // Build 7×24 grid (rows = days, cols = hours)
  const grid: HeatmapCell[][] = Array.from({ length: 7 }, () => []);
  for (const cell of cells) {
    if (grid[cell.day]) {
      grid[cell.day][cell.hour] = cell;
    }
  }

  return (
    <div className="relative overflow-x-auto">
      <div className="min-w-[680px]">
        {/* Hour labels */}
        <div className="mb-1 flex pl-10">
          {hourLabels.map((label, i) => (
            <div
              key={i}
              className="flex-1 text-center text-[10px] text-neutral-400"
            >
              {i % 3 === 0 ? label : ""}
            </div>
          ))}
        </div>

        {/* Grid rows */}
        {grid.map((row, dayIndex) => (
          <div key={dayIndex} className="flex items-center gap-1 mb-0.5">
            <span className="w-9 text-right text-xs text-neutral-400 pr-1">
              {dayLabels[dayIndex]}
            </span>
            <div className="flex flex-1 gap-0.5">
              {Array.from({ length: 24 }, (_, hourIndex) => {
                const cell = row[hourIndex] || {
                  day: dayIndex,
                  hour: hourIndex,
                  likes: 0,
                  replies: 0,
                  total: 0,
                };
                const value = getValue(cell);
                const level = getHeatLevel(value, maxValue);

                return (
                  <div
                    key={hourIndex}
                    className={`aspect-square flex-1 rounded ${heatColors[level]} cursor-pointer transition-opacity hover:opacity-80`}
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        cell,
                        x: rect.left + rect.width / 2,
                        y: rect.top - 8,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })}
            </div>
          </div>
        ))}

        {/* Legend */}
        <div className="mt-3 flex items-center justify-end gap-1 text-xs text-neutral-400">
          <span>Less</span>
          {heatColors.map((color, i) => (
            <div key={i} className={`h-3 w-3 rounded-sm ${color}`} />
          ))}
          <span>More</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-lg bg-black px-3 py-2 text-xs text-white shadow-lg"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className="font-medium">
            {dayLabels[tooltip.cell.day]} {hourLabels[tooltip.cell.hour]}
          </div>
          <div className="mt-0.5 text-neutral-300">
            {tooltip.cell.likes} likes &middot; {tooltip.cell.replies} replies
          </div>
        </div>
      )}
    </div>
  );
}
